import { useEffect, useState } from "react";
import { Sparkles, RefreshCw, AlertTriangle } from "lucide-react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const SESSION_FLOW = {
  romantic: [
    ["Arrival & Warm Welcome", "Greet both participants individually first — ease initial nerves before they meet."],
    ["The Introduction", "Facilitate the first hello. Keep it light, no pressure, no forced chemistry."],
    ["Icebreaker Walk & Talk", "Use Break the Ice prompts while walking to your first shooting spot."],
    ["Comfort-Building Poses", "Start with contact-free, candid poses only."],
    ["Check In On Boundaries", "Casually confirm comfort level before suggesting any contact-based poses."],
    ["Creative & Chemistry Shots", "If mutual boundaries allow it, move into closer or contact poses."],
    ["Wrap-Up & Thank You", "End on a high note — thank both and remind them their photos are on the way."],
  ],
  friendship: [
    ["Arrival & Warm Welcome", "Greet both participants individually first — ease initial nerves before they meet."],
    ["The Introduction", "Frame it as meeting a potential new friend, not a date."],
    ["Icebreaker Chat", "Use friendship-flavored prompts to get real conversation flowing."],
    ["Fun, Candid Poses", "Playful, energetic, low-pressure shots."],
    ["Shared-Activity Moment", "Do something together — walk, small game, sketch — and shoot candidly."],
    ["Optional Close Poses", "Only if both are mutually comfortable with it."],
    ["Wrap-Up & Thank You", "End on a high note — thank both and remind them their photos are on the way."],
  ],
  creative: [
    ["Arrival & Warm Welcome", "Greet both participants individually first — ease initial nerves before they meet."],
    ["The Introduction", "Frame the session as a creative collaboration between the two of them."],
    ["Icebreaker Chat", "Use creative-flavored prompts — discuss inspiration, vision, ideas."],
    ["Concept & Composition Poses", "Art-directed, storytelling shots built around a concept."],
    ["Experimental Poses", "Encourage creative risk-taking, always within stated boundaries."],
    ["Boundary Check-In", "Confirm comfort before introducing any contact-based creative concept."],
    ["Wrap-Up & Reflection", "Ask what moment stood out to them, then thank both participants."],
  ],
  default: [
    ["Arrival & Warm Welcome", "Greet both participants individually first — ease initial nerves before they meet."],
    ["The Introduction", "Facilitate the first hello. Keep it light and low-pressure."],
    ["Icebreaker Chat", "Use Break the Ice prompts to get real conversation flowing."],
    ["Comfort-Building Poses", "Start with contact-free, candid poses only."],
    ["Boundary Check-In", "Casually confirm comfort level before suggesting any contact-based poses."],
    ["Creative & Connection Shots", "If mutual boundaries allow it, move into closer or contact poses."],
    ["Wrap-Up & Thank You", "End on a high note — thank both and remind them their photos are on the way."],
  ],
};

const PromptList = ({ sessionId, category, matchId }) => {
  const [prompts, setPrompts] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchPrompts = () => {
    setLoading(true);
    api
      .get(`/sessions/${sessionId}/toolkit-prompts`, { params: { category, match_id: matchId || undefined } })
      .then(({ data }) => setPrompts(data.prompts || []))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchPrompts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, category, matchId]);

  const safePrompts = prompts.filter((p) => p.boundary_safe);
  const unsafePrompts = prompts.filter((p) => !p.boundary_safe);
  const visible = showAll ? prompts : safePrompts;

  return (
    <div data-testid={`toolkit-prompt-list-${category}`}>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <p className="text-xs font-medium text-midnight-black/60">
          {matchId ? "Filtered to this pair's mutual boundaries." : "No pair selected — showing contact-free ideas only."}
        </p>
        <Button data-testid={`shuffle-prompts-${category}`} onClick={fetchPrompts} disabled={loading} variant="outline" size="sm" className="rounded-full font-bold gap-1.5">
          <RefreshCw className="w-3.5 h-3.5" /> SHUFFLE
        </Button>
      </div>

      {visible.length === 0 ? (
        <p className="text-sm font-medium text-midnight-black/50" data-testid={`no-prompts-${category}`}>
          No boundary-safe prompts available right now.
        </p>
      ) : (
        <ul className="space-y-2">
          {visible.map((p) => (
            <li key={p.id} data-testid={`toolkit-prompt-${p.id}`} className="text-sm font-medium border border-soft-gray rounded-lg px-4 py-3 bg-warm-white">
              {p.text}
            </li>
          ))}
        </ul>
      )}

      {unsafePrompts.length > 0 && (
        <div className="mt-4">
          {!showAll ? (
            <button
              data-testid={`show-unconfirmed-prompts-${category}`}
              onClick={() => setShowAll(true)}
              className="flex items-center gap-1.5 text-[11px] font-bold tracking-widest text-midnight-black/50 hover:text-stranger-orange"
            >
              <AlertTriangle className="w-3.5 h-3.5" /> SHOW {unsafePrompts.length} MORE (UNCONFIRMED CONTACT)
            </button>
          ) : (
            <div className="mt-2 flex items-start gap-2 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid={`unconfirmed-warning-${category}`}>
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              These involve physical contact that hasn't been mutually confirmed by both participants' boundaries. Do not suggest these unless you've personally verified comfort with both people first.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const SessionToolkit = ({ sessionId, sessionType, matches, registrations }) => {
  const [matchId, setMatchId] = useState("");
  const flow = SESSION_FLOW[sessionType] || SESSION_FLOW.default;

  const matchLabel = (m) => {
    const names = m.booking_ids
      .map((bid) => registrations.find((r) => r.id === bid))
      .filter(Boolean)
      .map((r) => r.stranger?.first_name || "Stranger");
    return names.length ? names.join(" & ") : `Match ${m.id.slice(-6)}`;
  };

  return (
    <div className="mb-14 border border-soft-gray rounded-2xl p-6" data-testid="session-toolkit">
      <div className="flex items-center gap-2 mb-4">
        <Sparkles className="w-5 h-5 text-stranger-orange" />
        <h2 className="font-extrabold uppercase text-lg">Session Toolkit</h2>
      </div>

      <Tabs defaultValue="break_the_ice">
        <TabsList data-testid="toolkit-tabs" className="mb-4">
          <TabsTrigger data-testid="toolkit-tab-break-ice" value="break_the_ice">Break the Ice</TabsTrigger>
          <TabsTrigger data-testid="toolkit-tab-pose" value="pose_prompt">Pose & Prompt Ideas</TabsTrigger>
          <TabsTrigger data-testid="toolkit-tab-flow" value="flow">Session Flow</TabsTrigger>
        </TabsList>

        <TabsContent value="break_the_ice">
          <PromptList sessionId={sessionId} category="break_the_ice" matchId={matchId} />
        </TabsContent>

        <TabsContent value="pose_prompt">
          {matches.length > 0 && (
            <div className="mb-4 max-w-xs">
              <label className="block text-[10px] font-bold uppercase tracking-wide text-midnight-black/50 mb-1">Filter by matched pair</label>
              <Select value={matchId} onValueChange={(v) => setMatchId(v === "none" ? "" : v)}>
                <SelectTrigger data-testid="toolkit-match-select">
                  <SelectValue placeholder="No pair selected" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No pair selected</SelectItem>
                  {matches.map((m) => (
                    <SelectItem key={m.id} value={m.id}>{matchLabel(m)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <PromptList sessionId={sessionId} category="pose_prompt" matchId={matchId} />
        </TabsContent>

        <TabsContent value="flow">
          <ol className="space-y-3" data-testid="toolkit-session-flow">
            {flow.map(([title, desc], i) => (
              <li key={title} className="flex gap-3" data-testid={`toolkit-flow-step-${i}`}>
                <span className="w-6 h-6 rounded-full bg-midnight-black text-clean-white text-[11px] font-extrabold flex items-center justify-center shrink-0 mt-0.5">{i + 1}</span>
                <div>
                  <p className="font-extrabold text-sm uppercase">{title}</p>
                  <p className="text-sm font-medium text-midnight-black/60">{desc}</p>
                </div>
              </li>
            ))}
          </ol>
        </TabsContent>
      </Tabs>
    </div>
  );
};
